import React, { useState, useEffect, useContext } from 'react';
import '../view-archer-styles.css';

// material-ui
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';

import { Avatar, Button, Chip, Card, CardContent, CircularProgress, Grid, Link, IconButton, Paper, Popper, Stack, Typography, Table, TableBody, TableCell, TableContainer, TableRow} from '@mui/material/';
import { Drafts, EditOutlined, EditCalendarOutlined, PageviewOutlined, WatchLater, InsertCommentOutlined, Note } from '@mui/icons-material/';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

//components import
import DashboardPreviewDraftLetter from './DashboardPreviewDraftLetter';
import AlertInfo from '../../../dataObjects/AlertInfo';
import AlertInfoDialog from '../../../components/elements/AlertInfoDialog';
import ArcherLetter from '../dataObjects/ArcherLetter';
import { AuditorCommunicationContext } from '../view-archer';
import AuditNote from '../dataObjects/AuditNote';

interface Audit205Props {
    auditorId: number;
    displayStatus: string;
}

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: 'white',
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
        minWidth: 550,
    },
}));

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -3,
        marginLeft:10,
        top: 6,
        border: `2px solid ${theme.palette.background.paper}`,
        padding: '0 4px',
    },
}));

const initialValue = dayjs('2022-04-17');

function ServerDay(props: PickersDayProps<Dayjs> & { highlightedDays?: number[] }) {
    const { highlightedDays = [], day, outsideCurrentMonth, ...other } = props;

    const isSelected =
        !props.outsideCurrentMonth && highlightedDays.indexOf(props.day.date()) >= 0;

    return (
        <Badge
            key={props.day.toString()}
            overlap="circular"
            badgeContent={isSelected ? '🌚' : undefined}
        >
            <PickersDay {...other} outsideCurrentMonth={outsideCurrentMonth} day={day} />
        </Badge>
    );
}


const Audit205ScheduleManager = (props: Audit205Props) => {

    useEffect(() => {
        if(props.displayStatus==="active") {
            fetchDraftFollowUpLetters();
            fetchUpcomingFollowUpLetters();
        }
    }, [])

    const fetchDraftFollowUpLetters = async () => {
        await fetch(`/archer3/GetDraftFollowupLetters?auditorId=${props.auditorId}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetDraftFollowupLetters',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let letters = result.result;
                    let draftLetterSchedules = new Map();
                    letters.forEach((letter: ArcherLetter) => {
                        if (draftLetterSchedules.has(letter.date)) {
                            draftLetterSchedules.get(letter.date).push(letter);
                        }
                        else {
                            draftLetterSchedules.set(letter.date, [letter]);
                        }
                    });
                    setDraftLetterSchedules(draftLetterSchedules);
                    setLoadingIndicator('hidden');
                }
            })
    }

    const fetchUpcomingFollowUpLetters = async () => {
        await fetch(`/archer3/GetUpcomingFollowupLetters?auditorId=${props.auditorId}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: GetUpcomingFollowupLetters',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let letters = result.result;
                    let upcomingLetterSchedules = new Map();
                    letters.forEach((letter: ArcherLetter) => {
                        if (upcomingLetterSchedules.has(letter.date)) {
                            upcomingLetterSchedules.get(letter.date).push(letter);
                        }
                        else {
                            upcomingLetterSchedules.set(letter.date, [letter]);
                        }
                    });
                    setUpcomingLetterSchedules(upcomingLetterSchedules);
                    setLoadingIndicator('hidden');
                }
            })
    }

    const displayNotesDialog = (notes: AuditNote[]) => {

        return (
            <Stack spacing={2}>
                {
                    notes.map(
                        (note) =>
                            <Stack
                                direction="row" spacing={2}
                                justifyContent="flex-start"
                                alignItems="center"
                                className="note-card"
                            >
                                <Avatar className="small-avatar" sx={{ bgcolor: blue[200] }}><span className="note-initial">{note.userInitials}</span></Avatar>
                                <span className="note-date">{note.date}</span>
                                <span className="note-body">{note.body}</span>
                            </Stack>
                    )
                }
            </Stack>
        )
    }


    /* Initial state setup*/
    const [isPreviewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [upcomingLetterSchedules, setUpcomingLetterSchedules] = useState<Map<string, ArcherLetter[]>>();
    const [draftLetterSchedules, setDraftLetterSchedules] = useState<Map<string, ArcherLetter[]>>();
    const [currentLetterId, setCurrentLetterId] = useState(0);

    const previewLetter = (letterId: number) => {
        setCurrentLetterId(letterId);
        setPreviewDialogOpen(true);
    }

    //loading indicator
    const [isLoading, setLoadingIndicator] = useState('');

    /*Edit the letter*/
    const context = useContext(AuditorCommunicationContext);

    const setCurrentCpfIdAndLetterId = (cpfId: number, letterId: number) => {
        context.setCurrentLetterCpfId(cpfId);
        context.setCurrentLetterId(letterId);
    }

    /*Review the filer*/
    const setCurrentCpfId = (cpfId: number) => {
        context.setCurrentLetterCpfId(cpfId);
    }

    /*Hold follow up letters*/
    const [holdDate, setHoldDate] = useState<Dayjs | null>(dayjs(new Date()));
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const openHoldDateDialog = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const isHoleDateDialogOpen = Boolean(anchorEl);

    const updateHoldDate = (newValue: any, followUpDate: string, cpfId: number) => {
        setHoldDate(dayjs(newValue));
        holdFollowUpLetters(newValue, followUpDate, cpfId);
        setAnchorEl(null);
    }

    const holdFollowUpLetters = (newValue: any, followUpDate: string, cpfId:number) => {
        var params = [
            `cpfId=${cpfId}`,
            `holdDate=${dayjs(newValue).format('MM/DD/YYYY')}`,
            `followUpDate=${followUpDate}`
        ].join('&');
        fetch(`/archer3/HoldFollowUpLetters?${params}`)
            .then(result => result.json())
            .then(result => {
                if (result.isError) {
                    let info = {
                        type: 'error',
                        title: 'Error: HoldFollowUpLetters',
                        errorMessage: result.errorMessage,
                        secondaryErrorMessage: result.secondaryErrorMessage
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                }
                else {
                    let info = {
                        type: 'success',
                        title: 'Audit Follow up Rescheduled',
                        errorMessage: 'This Audit Follow up Letter has been successfully rescheduled',
                        secondaryErrorMessage: ''
                    };
                    setAlertInfo(info);
                    setAlertInfoDialog(true);
                    fetchUpcomingFollowUpLetters();
                }
            })
    }

    /*Error Handling*/
    const [isAlertInfoDialogOpen, setAlertInfoDialog] = useState(false);
    const [alertInfo, setAlertInfo] = useState<AlertInfo>();
    const closeAlertInfoDialog = () => {
        setAlertInfoDialog(false);
    }

    return (
        <div className="audit-205s-manager">
            <Grid container>
                <Grid item xs={8.5}>
                    {/*Rolling Or Monthly*/}
                    <Grid container
                        direction="row"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Grid item>
                            <Stack spacing={0} direction="row">
                                <Button variant="contained">Rolling</Button>
                                <Button variant="outlined">Monthly(20th each month)</Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    {/*Draft Audit Follow Up letters*/}
                    {
                        draftLetterSchedules &&
                        draftLetterSchedules.size > 0 &&
                        Array.from(draftLetterSchedules.keys()).map((date) =>
                            <Grid container sx={{ marginTop: 3 }} key={date}>
                                <Grid item xs={1.2} className="calendarDateColumn">
                                    {
                                        draftLetterSchedules.get(date)!.length > 3 && 
                                        <Badge badgeContent={draftLetterSchedules.get(date)!.length}
                                            anchorOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            color="success"
                                        >
                                            <div className="calendarDate">
                                                <em>{new Date(date).toLocaleDateString('default', { weekday: 'short' })}</em>
                                                <strong>{new Date(date).toLocaleString('default', { month: 'short' })} {new Date(date).getFullYear()}</strong>
                                                <span>{new Date(date).getDate()}</span>
                                            </div>
                                        </Badge>
                                    }
                                    {
                                        draftLetterSchedules.get(date)!.length <= 3 && 
                                        <div className="calendarDate">
                                            <em>{new Date(date).toLocaleDateString('default', { weekday: 'short' })}</em>
                                            <strong>{new Date(date).toLocaleString('default', { month: 'short' })} {new Date(date).getFullYear()}</strong>
                                            <span>{new Date(date).getDate()}</span>
                                        </div>
                                    }
                                    
                                </Grid>
                                <Grid item xs={10.8}>
                                    <Card variant="outlined" className="audit-follow-up-card">
                                        <CardContent className="card-content">
                                            <TableContainer sx={{ maxHeight: 400 }}>
                                                <Table sx={{ minWidth: 650 }} aria-label="draft letters table">
                                                    <TableBody>
                                                        {
                                                            draftLetterSchedules.get(date)!.map((letter: ArcherLetter) =>
                                                                <TableRow key={letter.cpfId}>
                                                                    <TableCell sx={{ width: 130 }}>
                                                                        <Avatar sx={{ bgcolor: '#ffc107' }} className="small-avatar"><Drafts fontSize="small" /></Avatar>
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "left!important" }}>
                                                                        <Stack spacing={2}>
                                                                            <Typography variant="body2"><b>{letter.cpfId} {letter.filerFullNameReverse}</b></Typography>
                                                                            <Typography variant="body2" className="issue-link-text" color="blue"><i>{letter.letterTemplateCode}</i></Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "left!important" }}>
                                                                        <>
                                                                            {
                                                                                letter.issueListings &&
                                                                                letter.issueListings.map((issue) =>
                                                                                    <Stack direction="row" spacing={2} key={issue.issueId}>
                                                                                        <span>
                                                                                            <span className={issue.status} >{issue.issueTypeCode} {issue.header}</span>
                                                                                            <Link key={issue.letterId} onClick={() => setCurrentCpfId(letter.cpfId)}> (<i>{issue.date}</i>)</Link>
                                                                                        </span>
                                                                                        {issue.notes.length > 0 &&
                                                                                            <HtmlTooltip title={displayNotesDialog(issue.notes)} placement="top">
                                                                                                <StyledBadge badgeContent={issue.notes.length} color="primary">
                                                                                                    <Note color="action" />
                                                                                                </StyledBadge>
                                                                                            </HtmlTooltip>
                                                                                        }
                                                                                        {issue.unreviewedResponses.length > 0 &&
                                                                                            <StyledBadge badgeContent={issue.unreviewedResponses.length} color="error">
                                                                                                <InsertCommentOutlined color="action" />
                                                                                            </StyledBadge>
                                                                                        }
                                                                                    </Stack>
                                                                                )
                                                                            }
                                                                        </>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1}>
                                                                            <PageviewOutlined fontSize="small" className="is-clickable" onClick={() => previewLetter(letter.id)} />
                                                                            <EditOutlined fontSize="small" className="is-clickable" onClick={() => setCurrentCpfIdAndLetterId(letter.cpfId, letter.id)} />
                                                                        </Stack>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )
                    }
                    {/*Upcoming Audit Follow Up letters*/}
                    {
                        upcomingLetterSchedules &&
                        upcomingLetterSchedules.size > 0 &&
                        Array.from(upcomingLetterSchedules.keys()).map((date) =>
                            <Grid container sx={{ marginTop: 3 }} key={date}>
                                <Grid item xs={1.2} className="calendarDateColumn">
                                    {
                                        upcomingLetterSchedules.get(date)!.length > 3 &&
                                        <Badge badgeContent={upcomingLetterSchedules.get(date)!.length}
                                            anchorOrigin={{
                                                vertical: 'top',
                                                horizontal: 'left',
                                            }}
                                            color="success"
                                        >
                                            <div className="calendarDate">
                                                <em>{new Date(date).toLocaleDateString('default', { weekday: 'short' })}</em>
                                                <strong>{new Date(date).toLocaleString('default', { month: 'short' })} {new Date(date).getFullYear()}</strong>
                                                <span>{new Date(date).getDate()}</span>
                                            </div>
                                        </Badge>
                                    }
                                    {
                                        upcomingLetterSchedules.get(date)!.length <= 3 &&
                                        <div className="calendarDate">
                                            <em>{new Date(date).toLocaleDateString('default', { weekday: 'short' })}</em>
                                            <strong>{new Date(date).toLocaleString('default', { month: 'short' })} {new Date(date).getFullYear()}</strong>
                                            <span>{new Date(date).getDate()}</span>
                                        </div>
                                    }
                                    
                                </Grid>
                                <Grid item xs={10.8}>
                                    <Card variant="outlined" className="audit-follow-up-card">
                                        <CardContent className="card-content">
                                            <TableContainer sx={{ maxHeight: 400 }}>
                                                <Table sx={{ minWidth: 650 }} aria-label="upcoming letters table">
                                                    <TableBody>
                                                        {
                                                            upcomingLetterSchedules.get(date)!.map((letter: ArcherLetter) =>
                                                                <TableRow key={letter.cpfId}>
                                                                    <TableCell sx={{ width: 130 }}>
                                                                        <Chip icon={<WatchLater />} label="Scheduled" color="primary" variant="outlined" />
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "left!important", maxWidth: 300 }}>
                                                                        <Stack spacing={2}>
                                                                            <Typography variant="body2"><b>{letter.cpfId} {letter.filerFullNameReverse}</b></Typography>
                                                                            <Typography variant="body2" className="issue-link-text" color="blue"><i>{letter.letterTemplateCode}</i></Typography>
                                                                        </Stack>
                                                                    </TableCell>
                                                                    <TableCell sx={{ textAlign: "left!important" }}>
                                                                        <>
                                                                            {
                                                                                letter.issueListings &&
                                                                                letter.issueListings.map((issue) =>
                                                                                    <Stack direction="row" spacing={1} key={issue.issueId}>
                                                                                        <span>
                                                                                            <span className={issue.status} >{issue.issueTypeCode} {issue.header}</span>  
                                                                                            <Link onClick={() => setCurrentCpfId(letter.cpfId)} key={issue.letterId}> (<i>{issue.date}</i>)</Link>
                                                                                        </span>
                                                                                        {issue.notes.length > 0 &&
                                                                                            <HtmlTooltip title={displayNotesDialog(issue.notes)} placement="top">
                                                                                                <StyledBadge badgeContent={issue.notes.length} color="primary">
                                                                                                    <Note color="action" />
                                                                                                </StyledBadge>
                                                                                            </HtmlTooltip>
                                                                                        }
                                                                                        {issue.unreviewedResponses.length > 0 &&
                                                                                            <StyledBadge badgeContent={issue.unreviewedResponses.length} color="error">
                                                                                                <InsertCommentOutlined color="action" />
                                                                                            </StyledBadge>
                                                                                        }
                                                                                    </Stack>
                                                                                )
                                                                            }
                                                                        </>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Stack direction="row" spacing={1}>
                                                                            <IconButton color="primary" className="is-clickable" title="Hold letter..." onClick={(event) => openHoldDateDialog(event)}>
                                                                                <EditCalendarOutlined fontSize="small" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                        <Popper
                                                                            open={isHoleDateDialogOpen}
                                                                            anchorEl={anchorEl}
                                                                        >
                                                                            <Paper sx={{ p: 2 }}>
                                                                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                                    <DemoContainer components={['DatePicker', 'DatePicker']}>
                                                                                        <DatePicker
                                                                                            label="Hold until..."
                                                                                            value={holdDate}
                                                                                            onChange={(newValue) => updateHoldDate(newValue, letter.date, letter.cpfId)}
                                                                                        />
                                                                                    </DemoContainer>
                                                                                </LocalizationProvider>
                                                                            </Paper>
                                                                        </Popper>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        }
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )
                    }
                  
                </Grid>
                {/*monthly review*/}
                <Grid item xs={3.5}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateCalendar />
                    </LocalizationProvider>
                </Grid>
            </Grid>
            <DashboardPreviewDraftLetter isPreviewDialogOpen={isPreviewDialogOpen} letterId={currentLetterId} closePreviewDialog={() => setPreviewDialogOpen(false)} />
            {
                alertInfo &&
                <AlertInfoDialog isAlertInfoDialogOpen={isAlertInfoDialogOpen} alertInfo={alertInfo} closeAlertInfoDialog={closeAlertInfoDialog}></AlertInfoDialog>
            }
            {/*loading indicator*/}
            <CircularProgress
                size={50}
                className={isLoading}
                sx={{
                    position: "fixed",
                    left: "50%",
                    top: "30%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 2
                }}
            />
        </div>
    )
}

export default Audit205ScheduleManager;